import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Input,
  Label,
  Spinner,
  formatFileSize,
} from "@opero/ui";
import {
  DownloadSimple,
  FileText,
  House,
  LockKey,
  Warning,
} from "@phosphor-icons/react";
import { polaris } from "../lib/polaris-client";

type Reason =
  | "OK"
  | "NOT_FOUND"
  | "EXPIRED"
  | "REVOKED"
  | "PASSWORD_REQUIRED"
  | "BAD_PASSWORD";

interface Resolution {
  valid: boolean;
  reason: Reason;
  resourceType: "document" | "folder" | null;
  resourceId: string | null;
  resourceName: string | null;
  permission: "VIEW" | "DOWNLOAD" | "EDIT" | null;
  downloadUrl: string | null;
  entries:
    | {
        id: string;
        name: string;
        entryType: "document" | "folder";
        fileSize?: number;
        mimeType?: string;
      }[]
    | null;
}

/**
 * A recipient opening a link has no other page to fall back to, so a request
 * that never answers has to become a visible failure with a retry rather than
 * an indefinite spinner. The generated `resolve` / `access` methods take no
 * per-request config, so these go through the SDK's own axios instance
 * (`polaris.http`) purely to set a timeout — auth, envelope unwrapping and
 * error shaping are unchanged.
 */
const RESOLVE_TIMEOUT_MS = 15000;

/**
 * The recipient side of a document public link.
 *
 * This lives on the marketing host rather than the workspace app because the
 * visitor is, by design, not signed in — the app host bounces an anonymous
 * request to `/login` before any page renders. The two endpoints behind it
 * (`resolve` / `access`) are the only unauthenticated document endpoints.
 */
export function SharedDocumentPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  const resolve = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setFailed(false);
    try {
      const { data } = await polaris.http.get<Resolution>(
        `/v1/documents/public/shares/${encodeURIComponent(token)}`,
        { timeout: RESOLVE_TIMEOUT_MS },
      );
      setResolution(data);
    } catch {
      // A network or gateway failure is not the same as an invalid link, and
      // telling a recipient their link is broken when the service is down
      // sends them to the wrong person for help.
      setFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void resolve();
  }, [resolve]);

  async function unlock() {
    if (!password) return;
    setIsSubmitting(true);
    try {
      const { data } = await polaris.http.post<Resolution>(
        `/v1/documents/public/shares/${encodeURIComponent(token)}`,
        // `download: true` mints the presigned URL in the same call, so a
        // correct password lands on a ready-to-download page rather than
        // requiring a second step.
        { password, download: true },
        { timeout: RESOLVE_TIMEOUT_MS },
      );
      setResolution(data);
    } catch {
      setFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function download() {
    // The URL from `resolve` is short-lived; re-mint it at click time rather
    // than trusting the one fetched when the page loaded.
    let url = resolution?.downloadUrl ?? null;
    try {
      const { data } = await polaris.http.post<Resolution>(
        `/v1/documents/public/shares/${encodeURIComponent(token)}`,
        { download: true, ...(password ? { password } : {}) },
        { timeout: RESOLVE_TIMEOUT_MS },
      );
      url = data.downloadUrl ?? url;
    } catch {
      // Fall back to the URL we already hold.
    }
    if (url) window.location.href = url;
  }

  if (isLoading) {
    return (
      <Shell>
        <Spinner className="size-6 text-slate-400" />
        <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
          Checking this link…
        </p>
      </Shell>
    );
  }

  if (failed) {
    return (
      <Shell>
        <Glyph tone="warning">
          <Warning />
        </Glyph>
        <Heading>Something went wrong</Heading>
        <Body>
          We couldn't check this link just now. Try again in a moment.
        </Body>
        <Actions>
          <Button onClick={resolve}>Try again</Button>
        </Actions>
      </Shell>
    );
  }

  const reason = resolution?.reason ?? "NOT_FOUND";

  if (reason === "PASSWORD_REQUIRED" || reason === "BAD_PASSWORD") {
    return (
      <Shell>
        <Glyph>
          <LockKey />
        </Glyph>
        <Heading>This link needs a password</Heading>
        <Body>
          The person who shared it can give you the password.
        </Body>

        <form
          className="mt-8 w-full max-w-xs text-left"
          onSubmit={(event) => {
            event.preventDefault();
            void unlock();
          }}
        >
          <Label htmlFor="share-password">Password</Label>
          <Input
            id="share-password"
            type="password"
            className="mt-1.5"
            value={password}
            autoFocus
            autoComplete="off"
            onChange={(event) => setPassword(event.target.value)}
            disabled={isSubmitting}
          />
          {reason === "BAD_PASSWORD" && (
            <p className="mt-2 text-sm text-danger">
              That password didn't work. Check it and try again.
            </p>
          )}
          <Button
            type="submit"
            className="mt-4 w-full"
            disabled={isSubmitting || !password}
          >
            {isSubmitting ? "Checking…" : "Unlock"}
          </Button>
        </form>
      </Shell>
    );
  }

  if (!resolution?.valid) {
    const copy = INVALID_COPY[reason] ?? INVALID_COPY.NOT_FOUND;
    return (
      <Shell>
        <Glyph tone="warning">
          <Warning />
        </Glyph>
        <Heading>{copy.title}</Heading>
        <Body>{copy.description}</Body>
        <Actions>
          <Button onClick={() => navigate("/")}>
            <House />
            Go to Opero
          </Button>
        </Actions>
      </Shell>
    );
  }

  const canDownload =
    resolution.permission === "DOWNLOAD" || resolution.permission === "EDIT";

  return (
    <Shell>
      <Glyph>
        <FileText />
      </Glyph>
      <Heading>{resolution.resourceName ?? "Shared document"}</Heading>
      <Body>
        {resolution.resourceType === "folder"
          ? "A folder has been shared with you."
          : "A document has been shared with you."}
      </Body>

      {resolution.entries && resolution.entries.length > 0 && (
        <ul className="mt-6 w-full max-w-md divide-y divide-slate-200 text-left dark:divide-slate-800">
          {resolution.entries.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center gap-2.5 py-2.5 text-sm text-slate-700 dark:text-slate-300"
            >
              <FileText className="size-4 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate">{entry.name}</span>
              {entry.fileSize != null && (
                <span className="shrink-0 text-xs tabular-nums text-slate-400">
                  {formatFileSize(entry.fileSize)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Actions>
        {canDownload ? (
          <Button onClick={() => void download()}>
            <DownloadSimple />
            Download
          </Button>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            This link allows viewing only — downloading is not permitted.
          </p>
        )}
      </Actions>
    </Shell>
  );
}

const INVALID_COPY: Record<string, { title: string; description: string }> = {
  NOT_FOUND: {
    title: "This link doesn't work",
    description:
      "It may have been mistyped or the share may have been removed. Ask the sender for a new link.",
  },
  EXPIRED: {
    title: "This link has expired",
    description:
      "Links can be set to stop working after a date. Ask the sender for a new one.",
  },
  REVOKED: {
    title: "This link has been turned off",
    description:
      "The person who shared it revoked access. Ask them to share it again.",
  },
};

/* ── Layout primitives ──
   opero-web still renders the hairline system described in
   `docs/linear-design-system.md`; the borderless contract applies to
   opero-app only. These match `NotFoundPage`'s centred column. */

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-[calc(100dvh-5rem)] flex-col items-center justify-center px-6 py-12 text-center">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-112 w-112 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-500/10 blur-[120px] dark:bg-primary-400/10"
      />
      {children}
    </div>
  );
}

function Glyph({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "warning";
}) {
  return (
    <div
      className={
        tone === "warning"
          ? "flex size-14 items-center justify-center rounded-2xl bg-amber-500/10 text-2xl text-amber-600 dark:text-amber-400 [&_svg]:size-7"
          : "flex size-14 items-center justify-center rounded-2xl bg-primary-500/10 text-2xl text-primary-600 dark:text-primary-400 [&_svg]:size-7"
      }
    >
      {children}
    </div>
  );
}

function Heading({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="mt-5 max-w-xl break-words text-2xl font-semibold text-slate-900 dark:text-slate-100">
      {children}
    </h1>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 max-w-md text-base text-slate-500 dark:text-slate-400">
      {children}
    </p>
  );
}

function Actions({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
      {children}
    </div>
  );
}

export default SharedDocumentPage;
